import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Badge, Button, Flex, Heading, Text } from "@radix-ui/themes";
import { IoSparkles } from "react-icons/io5";
import { ArrowUpIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { IconButton, TextField } from "@radix-ui/themes";
import { Loading } from "@geist-ui/core";
import { Converter } from "showdown";
import { Dialog } from "@/components/shared/dialog";
import styles from "./styles.module.scss";

const K8S_CLUSTER_KEY = "VVAjnrSEfndoVvVJTGhGaPsTbJBhiLxH";
const BASE_URL = "https://api-v3.aui.io/intelligent-agent/api/v1/external";

const converter = new Converter();

interface ChatMessage {
  id: string;
  role: "user" | "agent" | "system";
  content: string;
  executedWorkflows?: string[];
}

export const ApolloAgent = () => {
  const [apiKey, setApiKey] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [agentVariables, setAgentVariables] = useState("");
  const [showVariables, setShowVariables] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const variablesError = useMemo(() => {
    if (!agentVariables.trim()) return null;
    try {
      JSON.parse(agentVariables);
      return null;
    } catch {
      return "Invalid JSON";
    }
  }, [agentVariables]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleCreateTask = useCallback(async () => {
    if (!apiKey.trim()) return;

    setIsCreatingTask(true);
    try {
      const response = await fetch(`${BASE_URL}/tasks`, {
        method: "POST",
        headers: {
          "x-k8s-cluster-key": K8S_CLUSTER_KEY,
          "x-network-api-key": apiKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: "apollo-agent-user",
          task_origin_type: "third-party",
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create task: ${response.statusText}`);
      }

      const data = await response.json();
      setTaskId(data.id);
      setMessages([]);
    } catch (error) {
      console.error("Failed to create task:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "system",
          content: `Failed to create conversation: ${error instanceof Error ? error.message : "Unknown error"}`,
        },
      ]);
    } finally {
      setIsCreatingTask(false);
    }
  }, [apiKey]);

  const handleSendMessage = useCallback(async () => {
    if (!input.trim() || !taskId || isLoading) return;
    if (variablesError) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      let parsedVariables: Record<string, unknown> | undefined;
      if (agentVariables.trim()) {
        parsedVariables = JSON.parse(agentVariables);
      }

      const body: Record<string, unknown> = {
        task_id: taskId,
        text: userMessage.content,
      };

      if (parsedVariables) {
        body.agent_variables = parsedVariables;
      }

      const response = await fetch(`${BASE_URL}/message`, {
        method: "POST",
        headers: {
          "x-k8s-cluster-key": K8S_CLUSTER_KEY,
          "x-network-api-key": apiKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`);
      }

      const data = await response.json();

      const agentMessage: ChatMessage = {
        id: data.id || crypto.randomUUID(),
        role: "agent",
        content: data.text,
        executedWorkflows: data.executed_workflows,
      };

      setMessages((prev) => [...prev, agentMessage]);

      if (data.executed_workflows?.includes("conversation_forwarding")) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "system",
            content: "This conversation will be escalated to a human agent.",
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "system",
          content: `Failed to send message: ${error instanceof Error ? error.message : "Unknown error"}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, taskId, isLoading, apiKey, agentVariables, variablesError]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
      const textarea = e.target;
      textarea.style.height = "24px";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    },
    [],
  );

  const hasVariables = useMemo(
    () => agentVariables.trim().length > 0 && !variablesError,
    [agentVariables, variablesError],
  );

  const handleOpenVariablesDialog = useCallback(() => {
    setShowVariables(true);
  }, []);

  const handleCloseVariablesDialog = useCallback((open: boolean) => {
    setShowVariables(open);
  }, []);

  const handleApiKeyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setApiKey(e.target.value);
    },
    [],
  );

  const handleVariablesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAgentVariables(e.target.value);
    },
    [],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Heading size="4">Apollo Agent</Heading>
          {taskId && (
            <Badge color="green" size="1">
              Connected
            </Badge>
          )}
        </div>
        <div className={styles.headerRight}>
          <div className={styles.apiKeyInput}>
            <TextField.Root
              placeholder="Enter API Key..."
              type="password"
              value={apiKey}
              onChange={handleApiKeyChange}
              size="2"
            />
          </div>
          <Button
            size="2"
            highContrast
            onClick={handleCreateTask}
            disabled={!apiKey.trim() || isCreatingTask}
            loading={isCreatingTask}
          >
            New Conversation
          </Button>
        </div>
      </div>

      <div className={styles.chatContainer}>
        <div className={styles.messagesContainer}>
          {messages.length === 0 && (
            <div className={styles.emptyState}>
              <IoSparkles size={32} />
              <Text size="3" color="gray">
                {taskId
                  ? "Start a conversation by sending a message"
                  : "Enter your API key and create a new conversation to begin"}
              </Text>
            </div>
          )}

          {messages.map((msg) => {
            if (msg.role === "system") {
              return (
                <div key={msg.id} className={styles.messageRow}>
                  <div
                    className={`${styles.message} ${styles.systemMessage}`}
                  >
                    <ExclamationTriangleIcon />
                    <span>{msg.content}</span>
                  </div>
                </div>
              );
            }

            if (msg.role === "user") {
              return (
                <div
                  key={msg.id}
                  className={`${styles.messageRow} ${styles.messageRowUser}`}
                >
                  <div
                    className={`${styles.message} ${styles.userMessage}`}
                  >
                    {msg.content}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={`${styles.messageRow} ${styles.messageRowAgent}`}
              >
                <div className={styles.agentWrapper}>
                  <div className={styles.sparklesIcon}>
                    <IoSparkles />
                  </div>
                  <div className={styles.agentContent}>
                    <div
                      className={`${styles.message} ${styles.agentMessage}`}
                      dangerouslySetInnerHTML={{
                        __html: converter.makeHtml(msg.content),
                      }}
                    />
                    {msg.executedWorkflows &&
                      msg.executedWorkflows.length > 0 && (
                        <Flex gap="1" wrap="wrap">
                          {msg.executedWorkflows.map((wf) => (
                            <span key={wf} className={styles.workflowBadge}>
                              {wf}
                            </span>
                          ))}
                        </Flex>
                      )}
                  </div>
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div
              className={`${styles.messageRow} ${styles.messageRowAgent}`}
            >
              <div className={styles.agentWrapper}>
                <div className={styles.sparklesIcon}>
                  <IoSparkles />
                </div>
                <div
                  className={`${styles.message} ${styles.agentMessage} ${styles.loadingMessage}`}
                >
                  <Loading />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {taskId && (
          <div className={styles.inputContainer}>
            <div className={styles.inputRow}>
              <div className={styles.inputField}>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  rows={1}
                />
              </div>
              <Flex gap="2" align="center">
                <Button
                  size="1"
                  variant={hasVariables ? "soft" : "ghost"}
                  color={hasVariables ? "blue" : "gray"}
                  onClick={handleOpenVariablesDialog}
                >
                  <Text size="1">
                    {hasVariables ? "Variables *" : "Variables"}
                  </Text>
                </Button>
                <IconButton
                  size="2"
                  variant="soft"
                  radius="full"
                  color="gray"
                  highContrast
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading || !!variablesError}
                  loading={isLoading}
                >
                  <ArrowUpIcon />
                </IconButton>
              </Flex>
            </div>
          </div>
        )}

        <Dialog open={showVariables} onOpenChange={handleCloseVariablesDialog}>
          <Dialog.Content size="large" className={styles.variablesDialog}>
            <Flex justify="between" align="center" mb="4">
              <Dialog.Title>Agent Variables</Dialog.Title>
              <Dialog.Close />
            </Flex>
            <div className={styles.variablesEditor}>
              <label>Paste your JSON object below</label>
              <textarea
                value={agentVariables}
                onChange={handleVariablesChange}
                placeholder='{"name": "John", "email": "john@example.com", "subscription_status": "active"}'
                spellCheck={false}
              />
              {variablesError && (
                <span className={styles.variablesError}>
                  {variablesError}
                </span>
              )}
            </div>
            <Flex justify="end" mt="4">
              <Button
                size="2"
                highContrast
                onClick={() => setShowVariables(false)}
              >
                Done
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog>
      </div>
    </div>
  );
};
